import React, { useEffect, useRef, useState } from 'react';
import { http_get } from '../../../../utils/api_client';
import { API_ENDPOINTS } from '../../../../utils/endpoints';

// Attributes available to insert as merge tokens. Unlike "save to attribute"
// (which excludes company/role etc.), any attribute can be *inserted* into copy.
const FALLBACK = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'company', label: 'Company' },
  { key: 'role', label: 'Role' },
];

// A text field with a { } button that inserts a personalization token
// ({{attribute}}) at the caret. Used across the inspector content fields.
const TokenField = ({ value, onChange, placeholder, multiline = false }) => {
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tokens, setTokens] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    http_get(API_ENDPOINTS.GET_PERSONALIZATION_ATTRIBUTES)
      .then(({ data }) => {
        if (!alive || !data) return;
        setTokens([...(data.standard || FALLBACK), ...(data.custom || [])]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const insert = (key) => {
    const el = inputRef.current;
    const token = `{{${key}}}`;
    const cur = value || '';
    let next;
    let caret;
    if (el && typeof el.selectionStart === 'number') {
      const s = el.selectionStart;
      const e = el.selectionEnd;
      next = cur.slice(0, s) + token + cur.slice(e);
      caret = s + token.length;
    } else {
      next = cur + token;
      caret = next.length;
    }
    onChange(next);
    setOpen(false);
    requestAnimationFrame(() => {
      if (el) { el.focus(); try { el.setSelectionRange(caret, caret); } catch (_e) { /* no-op */ } }
    });
  };

  const InputTag = multiline ? 'textarea' : 'input';
  return (
    <div className="un-pe-tokenfield" ref={wrapRef}>
      <InputTag
        ref={inputRef}
        className="un-pe-field"
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="un-pe-tokenbtn"
        aria-label="Insert personalization token"
        title="Insert personalization"
        onClick={() => setOpen((o) => !o)}
      >{'{ }'}</button>
      {open && (
        <div className="un-pe-tokenmenu">
          <div className="un-pe-tokenmenu__h">Insert personalization</div>
          {tokens.map((t) => (
            <button key={t.key} type="button" className="un-pe-tokenmenu__i" onClick={() => insert(t.key)}>
              <span className="un-pe-tokenmenu__nm">{t.label}</span>
              <span className="un-pe-tokenmenu__k">{`{{${t.key}}}`}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TokenField;
